import * as mutationsTypes from "../types/mutations";
import { collections, auth, firestore } from "@/configs/firebase";
import { getSessionId } from "@/configs";
import { User } from "@/types";
import twilioClient from "@/lib/twilioClient";
import { getUserId } from "@/helpers";
import Vue from "vue";
import logger from "@/configs/logger";
// import LogRocket from "logrocket";
import axiosConfig from "@/configs/axios";
import * as api from "@/lib/api";
import taskRouter from "@/lib/taskRouter";
import isObject from "isobject";
import { getGroupAccess } from "@/helpers/permissions";
import { camelCase } from "lodash";
import featureConfig from "@/helpers/featureConfigs";

interface ActionContext {
  commit: Function;
  dispatch: Function;
  state: any;
}

interface LoginPayload {
  email: string;
  password: string;
}

function setUserAccess(
  context: ActionContext,
  access: { dataAccess: any; featureAccess: any }
) {
  logger("info", "access", access);
  context.commit(mutationsTypes.SET_USER_ACCESS, access);
}

function updateConfig(context: ActionContext, config: any) {
  const newConfig: any = { ...context.state.config };
  // on realtime update lauckdarkly returns a nested object
  // while we get a single object to directly get falgs.
  if (isObject(config)) {
    Object.keys(config).forEach(key => {
      if (key === "auto_accept") {
        const value = config[key];
        if (isObject(value)) {
          newConfig[camelCase(key)] = config[key].current;
        } else {
          newConfig[camelCase(key)] = config[key];
        }
      }
    });
  }
  context.commit(mutationsTypes.UPDATE_APP_CONFIG, newConfig);
}

async function setLastActive(context: ActionContext, value: any) {
  try {
    await collections.user.doc(getUserId(context.state.user)).update({
      last_active: value
    });
    logger("log", "last active time succesful set");
  } catch (err) {
    logger("error", "failed to set last active time");
    logger("error", err);
  }
}

// Setup everything critical for app to function
async function appLoad(context: ActionContext, { user }: any) {
  try {
    logger("log", "app loading...");
    context.commit(mutationsTypes.SET_APPLOADING, true);
    // disable logrocket
    // LogRocket.identify(user.uid, { email: user.email || "" });
    await axiosConfig();
    const userRecord: any = await getCurrentUser(context, user.uid);
    await collections.user.doc(user.uid).update({ sessionId: getSessionId() });
    const { data } = await api.updateTwilioTokens();

    if (userRecord) {
      (await featureConfig.init(userRecord, user.uid)).onChange(config => {
        logger("log", "feature config updated", config);
      });

      context.dispatch(
        "setUserAccess",
        getGroupAccess(userRecord.groups || [])
      );
    }

    context.dispatch("initTwilio", data.voice_token);
    context.dispatch("initTaskRouter", data.worker_token);
    context.dispatch("checkDisconnectedTasks");
    context.commit(mutationsTypes.SET_APPLOADING_ERROR, false);
  } catch (err) {
    logger("error", "app loading err");
    logger("error", err);
    context.commit(mutationsTypes.SET_APPLOADING_ERROR, true);
  } finally {
    context.commit(mutationsTypes.SET_APPLOADING, false);
  }
}

async function getCurrentUser(context: ActionContext, uid?: string) {
  try {
    const currentUser = auth().currentUser;
    const currentUserId = currentUser ? currentUser.uid : "";
    const userId = uid || currentUserId;

    if (!userId) {
      throw new Error("Failed to get user, uid is required.");
    }
    const ref = await collections.user.doc(uid).get();
    const user = ref.data();
    context.dispatch("setUser", ref.data());
    return user;
  } catch (err) {
    logger("error", "Failed to fetch user", err);
  }
}

async function login(_context: ActionContext, payload: LoginPayload) {
  try {
    const { email, password } = payload;
    await auth().signInWithEmailAndPassword(email, password);
  } catch (err) {
    //todo: improve error messages
    throw new Error(typeof err === "string" ? err : err.message);
  }
}

function cleanupSession(context: ActionContext) {
  logger("log", "cleaning up session");
  context.commit(mutationsTypes.RESET_STATE);
  twilioClient.device.destroy();
}

async function logout(context: ActionContext) {
  try {
    await auth().signOut();
  } catch (err) {
    logger("error", "Logout failed", err);
  }
}

function setUser(context: ActionContext, user: User | {}) {
  context.commit(mutationsTypes.ADD_USER, user);
}

async function toggleAgentReady(
  { dispatch }: ActionContext,
  available: boolean
) {
  try {
    taskRouter.setAvailability(available, (err: any, worker: any) => {
      if (err) {
        Vue.toasted
          .error(`Faiiled to go ${available ? "offline" : "online"}`)
          .goAway(2000);
        logger("error", err);
      } else {
        if (available) {
          logger("log", "now online.");
          Vue.toasted
            .success("Calls incoming! Do your best and kill it!")
            .goAway(3000);
          dispatch("setLastActive", null);
        } else {
          logger("log", "now offline.");
          Vue.toasted
            .info("Taking a break? You are now offline, see ya later!")
            .goAway(2000);
          dispatch("setLastActive", firestore.Timestamp.now());
        }
      }
    });
  } catch (error) {
    logger("error", "toggleAgentReady - error", error);
  }
}

async function getDIDs() {
  try {
    const snap = await collections.dids.get();
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (err) {
    Vue.toasted.error("Get DIDs failed" + err);
    logger("error", "Logout failed", err);
  }
}

async function updateAgent(
  _context: ActionContext,
  payload: { data: any; userId: string; notify: boolean }
) {
  try {
    logger("info", "Updating User");
    logger("info", payload.userId);
    logger("info", payload.data);
    await collections.user.doc(payload.userId).update(payload.data);
    if (payload.notify) {
      Vue.toasted.success("User successfully updated").goAway(2000);
    }
  } catch (err) {
    logger("error", "user update error", err);
    Vue.toasted.error("User update error").goAway(2000);
  }
}

export default {
  cleanupSession,
  toggleAgentReady,
  logout,
  login,
  setUser,
  appLoad,
  setLastActive,
  getDIDs,
  updateAgent,
  updateConfig,
  setUserAccess
};
